import React, { useEffect, useRef, useState, useMemo, memo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserNotification, Source } from "../../features/user/userSlice";
import { useToggleUserNotificationReadMutation } from "../../services/userAPI";
import { convertUtcToLocalTime } from "../../utils/DateFormating";
import { useReadAllNotificationsMutation } from "../../services/userAPI";
import { LanguageText } from "../../dictionary/UserText";
import useGetLanguage from "../../utils/useGetLanguage";
import { useAppSelector } from "../../app/hooks";
import { selectDevices } from "../../features/devices/deviceSlice";
import { selectDashboards } from "../../features/dashboards/dashboardSlice";
import { selectGroups } from "../../features/groups/groupSlice";

interface NotificationListProp {
  notification: UserNotification;
  handleToggleRead: (
    event: React.MouseEvent<HTMLOrSVGElement>,
    notificationID: string
  ) => Promise<void>;
  handleNotificationClick: (
    event: React.MouseEvent<HTMLLIElement>,
    notification: UserNotification
  ) => void;
}
interface DropdownNotificationProps {
  notifications: Object | null;
}

const NotificationList = memo(
  ({
    notification,
    handleToggleRead,
    handleNotificationClick,
  }: NotificationListProp): JSX.Element => {
    const Language: LanguageText = useGetLanguage({ fileName: "UserText" });
    const isUnread = !notification.read;

    return (
      <li
        className={`flex flex-col gap-2.5 border-t border-stroke p-4 transition-all duration-200 dark:border-strokedark cursor-pointer ${
          isUnread
            ? "bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 border-l-4 border-l-blue-500"
            : "hover:bg-gray-50 dark:hover:bg-meta-4/50"
        }`}
        onClick={(event) => handleNotificationClick(event, notification)}
      >
        <div className="relative flex flex-row gap-3 items-start">
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm leading-relaxed ${
                isUnread ? "font-medium text-gray-900" : "text-gray-700"
              }`}
            >
              {notification.content.length > 255
                ? `${notification?.content.slice(0, 255)}...`
                : notification?.content}
            </p>
            <p className="text-xs text-gray-500 mt-1.5">
              {convertUtcToLocalTime(notification?.timestamp)}
            </p>
          </div>

          {/* Improved read/unread toggle button */}
          <button
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleToggleRead(event, notification._id);
            }}
            onMouseDown={(event) => {
              // Stop propagation to prevent dropdown from closing
              event.stopPropagation();
            }}
            className={`group/btn flex-shrink-0 ml-2 p-1.5 rounded-full transition-all duration-200 ${
              isUnread
                ? "bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60"
                : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
            }`}
            title={
              isUnread
                ? Language?.notifications?.markAsRead || "Mark as read"
                : Language?.notifications?.markAsUnread || "Mark as unread"
            }
          >
            {isUnread ? (
              <svg
                className="w-4 h-4 text-blue-600 dark:text-blue-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <circle cx="12" cy="12" r="10" />
              </svg>
            ) : (
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
              </svg>
            )}
          </button>
        </div>
      </li>
    );
  }
);

const getFormattedNotifications = (notifications: Object | null): UserNotification[] => {
  const notificationsArray: UserNotification[] = [];

  if (notifications) {
    for (const [key, value] of Object.entries(notifications)) {
      var newTimestamp = new Date(value.timestamp).toDateString();
      var firstIndex = newTimestamp.indexOf(" ");
      var lastIndex = newTimestamp.lastIndexOf(" ");
      var formattedDate =
        newTimestamp.substring(firstIndex, lastIndex) +
        ", " +
        newTimestamp.substring(lastIndex);

      var formattedValue = {
        ...value,
        // Ensure read is a boolean; default to false when missing from server
        read: (value as any).read === true,
        date: formattedDate,
      } as UserNotification;
      notificationsArray.push(formattedValue);
    }

    notificationsArray.sort(
      (a: any, b: any) => Date.parse(b.timestamp) - Date.parse(a.timestamp)
    );
  }
  return notificationsArray;
};

const DropdownNotification = memo(({ notifications }: DropdownNotificationProps) => {
  const Language: LanguageText = useGetLanguage({ fileName: "UserText" });
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifying, setNotifying] = useState(true);
  const [visibleNotifications, setVisibleNotifications] = useState<UserNotification[]>(
    []
  );

  const trigger = useRef<any>(null);
  const dropdown = useRef<any>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [toggleRead] = useToggleUserNotificationReadMutation();
  const [readAllNotifications] = useReadAllNotificationsMutation();

  const groups = useAppSelector(selectGroups).groups;
  const devices = useAppSelector(selectDevices).devices;
  const dashboards = useAppSelector(selectDashboards).dashboards;

  const isGroup = (groupID: string) => {
    if (!groupID || !groups) return false;
    return groupID in groups;
  };
  const isDevice = (deviceID: string) => {
    if (!deviceID || !devices) return false;
    return !!devices.find((device) => device.id === deviceID);
  };

  const isDashboard = (dashboardID: string) => {
    if (!dashboardID || !dashboards) return false;
    return dashboardID in dashboards;
  };

  const notificationsArray = useMemo(
    () => getFormattedNotifications(notifications),
    [notifications]
  );

  const handleReadAll = useCallback(
    async () => await readAllNotifications({ language: Language || null }),
    [readAllNotifications, Language]
  );

  const hasUnread: boolean = useMemo(
    () => notificationsArray.some((notification) => notification.read !== true),
    [notificationsArray]
  );

  const handleToggleDropdown = useCallback(() => {
    setNotifying(false);
    setDropdownOpen((prevOpen) => !prevOpen);
  }, []);

  const handleToggleRead = useCallback(
    async (event: React.MouseEvent<HTMLOrSVGElement>, notificationID: any) => {
      event.preventDefault();
      event.stopPropagation();
      notificationID &&
        (await toggleRead({
          notificationID: notificationID,
          language: Language,
        }));
    },
    [toggleRead, Language]
  );

  const handleNotificationClick = useCallback(
    async (event: React.MouseEvent<HTMLLIElement>, notification: UserNotification) => {
      event.preventDefault();
      const { _id: notificationID, link_id, link_info, read } = notification;

      let path: string | null = null;
      switch (link_info) {
        case Source.DEVICE:
          isDevice(link_id) ? (path = `device/${link_id}`) : (path = null);
          break;
        case Source.DASHBOARD:
          isDashboard(link_id) ? (path = `dashboard/${link_id}`) : (path = null);
          break;
        case Source.GROUP:
          isGroup(link_id) ? (path = `group/${link_id}`) : (path = null);
          break;
        default:
          path = null;
      }

      if (path) {
        navigate(path);
      }

      if (!read) {
        await handleToggleRead(event, notificationID);
      }
    },
    [navigate, handleToggleRead]
  );

  const handleScroll = useCallback(() => {
    if (listRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleNotifications((prev) =>
          prev.concat(notificationsArray.slice(prev.length, prev.length + 10))
        );
      }
    }
  }, [notificationsArray]);

  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      if (!dropdown.current || !trigger.current) return;
      const target = event.target as Node;

      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      ) {
        return;
      }
      setDropdownOpen(false);
    };

    // Use mousedown instead of click to catch the event earlier
    document.addEventListener("mousedown", clickHandler);
    return () => document.removeEventListener("mousedown", clickHandler);
  }, [dropdownOpen]);

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };

    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  useEffect(() => {
    // Preserve existing visible notifications order when toggling read status
    // Only reset if the array length changed significantly or if it's the first load
    if (visibleNotifications.length === 0) {
      setVisibleNotifications(notificationsArray.slice(0, 20));
    } else {
      // Map existing visible notifications to their new state, preserving order
      const existingIds = new Set(visibleNotifications.map((n) => n._id));
      const updatedVisible = visibleNotifications
        .map((visible) => {
          const updated = notificationsArray.find((n) => n._id === visible._id);
          return updated || visible;
        })
        .filter((n) => notificationsArray.some((na) => na._id === n._id));

      // Add any new notifications that weren't in the visible list
      const newNotifications = notificationsArray
        .filter((n) => !existingIds.has(n._id))
        .slice(0, 20 - updatedVisible.length);

      setVisibleNotifications([...updatedVisible, ...newNotifications].slice(0, 20));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsArray]);

  return (
    <>
      <Link
        ref={trigger}
        onClick={handleToggleDropdown}
        to="#"
        className="relative hover:text-primary inline-flex items-center p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
      >
        {hasUnread && (
          <span className="absolute -top-1 -right-1 flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white z-10 shadow-lg border-2 border-white dark:border-gray-800 animate-pulse">
            {
              notificationsArray.filter((notification) => notification.read !== true)
                .length
            }
          </span>
        )}
        <svg
          className="relative w-6 h-6"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"></path>
        </svg>
      </Link>
      <div
        ref={dropdown}
        className={`absolute right-0 mt-2.5 mr-3 flex h-[70vh] w-75 min-w-settings flex-col rounded-lg border border-gray-200 bg-white shadow-xl dark:border-strokedark dark:bg-boxdark sm:right-0 sm:w-80 ${
          dropdownOpen ? "block" : "hidden"
        }`}
      >
        {/* Header */}
        <div className="relative px-4 py-3.5 border-b border-gray-200 dark:border-strokedark bg-gray-50 dark:bg-meta-4/20 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                {Language?.notifications?.title || "Notifications"}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {Language?.notifications?.subTitle || "LATEST"}
              </p>
            </div>
            {hasUnread && (
              <button
                onClick={handleReadAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
                title={Language?.notifications?.readAll || "Mark all as read"}
              >
                <svg
                  className="w-3.5 h-3.5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                {Language?.notifications?.readAll || "Read all"}
              </button>
            )}
          </div>
        </div>

        {/* Notification List */}
        <ul
          ref={listRef}
          onScroll={handleScroll}
          className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        >
          {visibleNotifications.length === 0 ? (
            <li className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <svg
                className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {Language?.notifications?.emptyNotificationMsg || "No notifications"}
              </p>
            </li>
          ) : (
            <>
              {visibleNotifications.map((notification) => {
                return (
                  <NotificationList
                    key={notification._id}
                    notification={notification}
                    handleToggleRead={handleToggleRead}
                    handleNotificationClick={handleNotificationClick}
                  />
                );
              })}
              {visibleNotifications.length > 0 && (
                <li className="border-t border-gray-200 dark:border-strokedark">
                  <div className="text-xs py-6 px-6 text-gray-500 dark:text-gray-400 text-center">
                    <p>
                      {Language?.notifications?.footerMsg ||
                        "That's all your notifications from the last 30 days"}
                    </p>
                  </div>
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    </>
  );
});

export default DropdownNotification;
